import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as RadioPrimitive from '@radix-ui/react-radio-group';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { Check, Minus } from 'lucide-react';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** Checkbox (supports `checked="indeterminate"`). Pair with `label` for a clickable label; 44px hit area on touch. */
export const Checkbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & { readonly label?: ReactNode; readonly description?: ReactNode }
>(({ className, label, description, id, ...props }, ref) => {
  const gen = useId();
  const cid = id ?? `cb${gen}`;
  const box = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={cid}
      className={cn(
        'peer relative inline-flex size-5 shrink-0 items-center justify-center rounded border border-border-strong bg-surface text-primary-foreground',
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary',
        'after:absolute after:-inset-3 after:content-[""] disabled:opacity-50',
        focusRing,
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {props.checked === 'indeterminate' ? <Minus className="size-3.5" aria-hidden /> : <Check className="size-3.5" aria-hidden />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return box;
  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">{box}</div>
      <label htmlFor={cid} className="cursor-pointer text-sm">
        <span className="font-medium text-text">{label}</span>
        {description ? <span className="block text-text-muted">{description}</span> : null}
      </label>
    </div>
  );
});
Checkbox.displayName = 'Checkbox';

export interface SwitchProps extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  readonly label?: ReactNode;
  readonly description?: ReactNode;
}

/** On/off toggle for settings that apply immediately. With `label`, renders a full-width settings row. */
export const Switch = forwardRef<ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(({ className, label, description, id, ...props }, ref) => {
  const gen = useId();
  const sid = id ?? `sw${gen}`;
  const descId = description ? `${sid}-d` : undefined;
  const control = (
    <SwitchPrimitive.Root
      ref={ref}
      id={sid}
      aria-describedby={descId}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-fast',
        'bg-border-strong/70 data-[state=checked]:bg-primary disabled:cursor-not-allowed disabled:opacity-50',
        'after:absolute after:-inset-2.5 after:content-[""]',
        focusRing,
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block size-5 rounded-full bg-surface shadow-sm transition-transform duration-fast data-[state=checked]:translate-x-5" />
    </SwitchPrimitive.Root>
  );
  if (!label) return control;
  return (
    <div className="flex items-start justify-between gap-6 py-3">
      <div className="min-w-0">
        <label htmlFor={sid} className="cursor-pointer text-sm font-medium text-text">
          {label}
        </label>
        {description ? (
          <p id={descId} className="mt-0.5 text-sm text-text-muted">
            {description}
          </p>
        ) : null}
      </div>
      {control}
    </div>
  );
});
Switch.displayName = 'Switch';

export interface RadioOption<V extends string> {
  readonly value: V;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly icon?: ReactNode;
  readonly disabled?: boolean;
}

export interface RadioGroupProps<V extends string> extends Omit<ComponentPropsWithoutRef<typeof RadioPrimitive.Root>, 'onValueChange' | 'value' | 'defaultValue'> {
  readonly options: readonly RadioOption<V>[];
  readonly value?: V;
  readonly defaultValue?: V;
  readonly onValueChange?: (value: V) => void;
  /** `cards` renders large selectable tiles (onboarding, plan pickers). */
  readonly variant?: 'list' | 'cards';
}

/** Single choice from a short list. Arrow keys move between options. */
export const RadioGroup = <V extends string>({ options, variant = 'list', className, onValueChange, ...props }: RadioGroupProps<V>) => (
  <RadioPrimitive.Root
    className={cn(variant === 'cards' ? 'grid gap-3 sm:grid-cols-2' : 'flex flex-col gap-2', className)}
    onValueChange={(v) => onValueChange?.(v as V)}
    {...props}
    // passing `value` at all means controlled; "nothing chosen yet" is '' (not undefined) so React doesn't warn on the first pick
    {...('value' in props ? { value: props.value ?? '' } : {})}
  >
    {options.map((o) =>
      variant === 'cards' ? (
        <RadioPrimitive.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          className={cn(
            'group flex min-h-touch items-start gap-3 rounded-lg border border-border bg-surface p-4 text-left transition-colors duration-fast',
            'hover:border-border-strong data-[state=checked]:border-primary data-[state=checked]:bg-primary-soft data-[state=checked]:ring-1 data-[state=checked]:ring-primary',
            focusRing,
          )}
        >
          {o.icon ? <span className="mt-0.5 text-primary [&_svg]:size-5">{o.icon}</span> : null}
          <span className="min-w-0">
            <span className="block font-medium text-text">{o.label}</span>
            {/* muted grey fails 4.5:1 on the tinted checked card, so the selected card's description uses body text */}
            {o.description ? <span className="mt-0.5 block text-sm text-text-muted group-data-[state=checked]:text-text">{o.description}</span> : null}
          </span>
        </RadioPrimitive.Item>
      ) : (
        <label key={o.value} className="flex min-h-10 cursor-pointer items-start gap-3 text-sm">
          <RadioPrimitive.Item
            value={o.value}
            disabled={o.disabled}
            className={cn('mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-border-strong bg-surface data-[state=checked]:border-primary', focusRing)}
          >
            <RadioPrimitive.Indicator className="size-2.5 rounded-full bg-primary" />
          </RadioPrimitive.Item>
          <span>
            <span className="font-medium text-text">{o.label}</span>
            {o.description ? <span className="block text-text-muted">{o.description}</span> : null}
          </span>
        </label>
      ),
    )}
  </RadioPrimitive.Root>
);
